import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

/**
 * Iter 182 — Privacy Policy.
 *
 * Linked from Profile → Notifications. Explains how AOP handles the
 * mobile number, email address, and device token used for messaging
 * preferences.
 */
export default function Privacy() {
    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
            <Link to="/profile" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4" data-testid="privacy-back">
                <ArrowLeft className="h-4 w-4" /> Back to profile
            </Link>
            <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className="h-7 w-7 text-primary" />
                <h1 className="font-heading text-3xl sm:text-4xl font-black">Privacy Policy</h1>
            </div>
            <p className="text-muted-foreground">Last updated: October 2026</p>

            <div className="prose prose-slate max-w-none mt-8 space-y-6 text-sm leading-relaxed">
                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">1. What we collect</h2>
                    <p>
                        To send the SMS, email, and push notifications you opt into, we
                        store:
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Your mobile number (for SMS and 2FA codes).</li>
                        <li>Your email address (for account email + marketing categories you opt into).</li>
                        <li>Your OneSignal device token / Player ID (for push notifications).</li>
                        <li>Your category-level preferences (which toggles are on/off).</li>
                    </ul>
                </section>

                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">2. How we use it</h2>
                    <p>
                        We use the data strictly to deliver the messages you asked for and
                        the account-critical messages (password resets, security alerts,
                        RSVP receipts) that keep your account working. We do not sell,
                        rent, or share your contact information with third parties for
                        their own marketing purposes.
                    </p>
                </section>

                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">3. Who processes it</h2>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><b>Brevo</b> — email + SMS delivery.</li>
                        <li><b>OneSignal</b> — push notification delivery.</li>
                        <li><b>Twilio</b> — phone-number verification where enabled.</li>
                    </ul>
                    <p>
                        Each processor only receives the minimum data needed to deliver
                        the message (e.g. email + subject + body for Brevo, device token
                        + payload for OneSignal).
                    </p>
                </section>

                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">4. Opting out</h2>
                    <p>
                        You can opt out of any category at any time:
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><b>SMS:</b> toggle off in Profile → Notifications, or reply <b>STOP</b> to any text.</li>
                        <li><b>Email:</b> toggle individual categories in Email preferences, or click Unsubscribe in any email footer.</li>
                        <li><b>Push:</b> click Disable on the Push notifications card, or turn notifications off in your browser/OS settings.</li>
                    </ul>
                    <p>
                        When you opt out, we remove the relevant token or flag within 24
                        hours. Historical delivery logs (needed for debugging and
                        compliance) are kept for 90 days then deleted.
                    </p>
                </section>

                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">5. Account deletion</h2>
                    <p>
                        Email <a href="mailto:support@alphaomegaphi.org" className="text-primary underline">support@alphaomegaphi.org</a> to request
                        deletion of your account. We purge all personal data within 30
                        days of a verified request, except where retention is required by
                        law (e.g. donation receipts for IRS reporting).
                    </p>
                </section>

                <section>
                    <h2 className="font-heading text-xl font-bold text-primary">6. Contact</h2>
                    <p>
                        Questions about this policy or how your data is handled? Email
                        <a href="mailto:support@alphaomegaphi.org" className="text-primary underline mx-1">support@alphaomegaphi.org</a>
                        or write to Alpha Omega Phi Military Fraternity &amp; Sorority, Inc.
                    </p>
                </section>
            </div>
        </div>
    );
}
